import { useState, useEffect, createContext } from "react"
import Client from "shopify-buy"

const client = Client.buildClient({
  domain: process.env.GATSBY_SHOPIFY_STORE_URL,
  storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
})

const defaultValues = {
  cart: [],
  isOpen: false,
  loading: false,
  error: null,
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  clearError: () => {},
  client,
  checkout: {
    lineItems: [],
  },
}

export const StoreContext = createContext(defaultValues)

const isBrowser = typeof window !== `undefined`
const localStorageKey = `shopify_checkout_id`

const ContextProvider = ({ children }) => {
  const [checkout, setCheckout] = useState(defaultValues.checkout)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [didJustAddToCart, setDidJustAddToCart] = useState(false)
  const [isCartDrawerVisible, setCartDrawerVisibility] = useState(false)
  const [isMenuDrawerVisible, setMenuDrawerVisibility] = useState(false)
  const clearError = () => setError(null)

  const setCheckoutItem = (checkout) => {
    if (isBrowser) {
      localStorage.setItem(localStorageKey, checkout.id)
    }
    setCheckout(checkout)
  }

  useEffect(() => {
    const initializeCheckout = async () => {
      const existingCheckoutID = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null

      clearError()

      if (existingCheckoutID && existingCheckoutID !== `null`) {
        try {
          const existingCheckout =
            await client.checkout.fetch(existingCheckoutID)
          if (!existingCheckout.completedAt) {
            setCheckoutItem(existingCheckout)
            return
          }
        } catch (e) {
          localStorage.setItem(localStorageKey, null)
        }
      }

      try {
        const newCheckout = await client.checkout.create()
        setCheckoutItem(newCheckout)
      } catch (e) {
        setError("Unable to initialize checkout right now.")
      }
    }

    initializeCheckout()
  }, [])

  const addVariantToCart = async (variantId, quantity) => {
    setLoading(true)
    clearError()

    const checkoutID = checkout.id

    const lineItemsToUpdate = [
      {
        variantId,
        quantity: parseInt(quantity, 10),
      },
    ]

    try {
      const res = await client.checkout.addLineItems(
        checkoutID,
        lineItemsToUpdate,
      )
      setCheckout(res)
      setCartDrawerVisibility(true)
      setDidJustAddToCart(true)
      setTimeout(() => setDidJustAddToCart(false), 2000)
      return true
    } catch (e) {
      setError("Unable to add this item to your cart right now.")
      return false
    } finally {
      setLoading(false)
    }
  }

  const removeLineItem = async (checkoutID, lineItemID) => {
    setLoading(true)
    clearError()

    try {
      const res = await client.checkout.removeLineItems(checkoutID, [
        lineItemID,
      ])
      setCheckout(res)
    } catch (e) {
      setError("Unable to remove this item from your cart right now.")
    } finally {
      setLoading(false)
    }
  }

  const updateLineItem = async (checkoutID, lineItemID, quantity) => {
    setLoading(true)
    clearError()

    const lineItemsToUpdate = [
      { id: lineItemID, quantity: parseInt(quantity, 10) },
    ]

    try {
      const res = await client.checkout.updateLineItems(
        checkoutID,
        lineItemsToUpdate,
      )
      setCheckout(res)
    } catch (e) {
      setError("Unable to update cart quantity right now.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        removeLineItem,
        updateLineItem,
        checkout,
        loading,
        error,
        clearError,
        didJustAddToCart,
        isCartDrawerVisible,
        setCartDrawerVisibility,
        isMenuDrawerVisible,
        setMenuDrawerVisibility,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}

export default ContextProvider
